import React, { useState, useEffect, useRef } from 'react';
import radiologyResultService from '../../services/radiologyResultService';
import radiologyMasterConfigService from '../../services/radiologyMasterConfigService';
import RadiologyRichEditor from './RadiologyRichEditor';
import RadiologyTemplatePreviewModal from './RadiologyTemplatePreviewModal';
import RadiologyVersionHistoryDrawer from './RadiologyVersionHistoryDrawer';
import './RadiologyTemplateEditorView.css';

const REUSABLE_SECTIONS = [
  { label: 'Clinical History', html: '<p><strong>CLINICAL HISTORY & INDICATION:</strong></p><p>Patient presents for evaluation. No previous imaging available for comparison.</p><p><br></p>' },
  { label: 'Technique', html: '<p><strong>TECHNIQUE & PROTOCOL:</strong></p><p>Standard multiplanar volumetric acquisition was performed according to institutional protocol without complications.</p><p><br></p>' },
  { label: 'Comparison', html: '<p><strong>COMPARISON:</strong></p><p>Compared with previous examination dated: {{STUDY_DATE}}.</p><p><br></p>' },
  { label: 'Findings', html: '<p><strong>FINDINGS:</strong></p><p>The examined anatomical structures demonstrate normal morphology and signal characteristics without acute focal abnormality.</p><p><br></p>' },
  { label: 'Impression', html: '<p><strong>IMPRESSION:</strong></p><p>1. Normal examination within age limits.</p><p>2. No acute or focal pathology identified.</p><p><br></p>' },
  { label: 'Recommendations', html: '<p><strong>RECOMMENDATIONS:</strong></p><p>Clinical correlation recommended. Follow-up as clinically indicated.</p><p><br></p>' }
];

const DYNAMIC_FIELDS = [
  { label: 'Patient Name', token: '{{PATIENT_NAME}}' },
  { label: 'Age', token: '{{AGE}}' },
  { label: 'Sex', token: '{{SEX}}' },
  { label: 'Patient ID', token: '{{PATIENT_ID}}' },
  { label: 'Study Date', token: '{{STUDY_DATE}}' },
  { label: 'Report Date', token: '{{REPORT_DATE}}' },
  { label: 'Accession No', token: '{{ACCESSION_NUMBER}}' },
  { label: 'Referring Doctor', token: '{{REFERRING_DOCTOR}}' },
  { label: 'Radiologist Name', token: '{{RADIOLOGIST_NAME}}' }
];

export default function RadiologyTemplateEditorView({
  templateId, // null if creating new
  investigation, // { investigationId, investigationName, departmentName, testCode }
  initialTemplate, // optional pre-loaded template object
  onBack,
  onSaveSuccess
}) {
  const [formData, setFormData] = useState({
    templateId: templateId || null,
    investigationId: investigation?.investigationId || '',
    investigationName: investigation?.investigationName || '',
    templateTitle: '',
    templateDesc: '',
    scope: 'SHARED',
    doctorName: '',
    doctorId: null,
    isDefault: false,
    isActive: true,
    version: 1
  });

  const [initialData, setInitialData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  // Modals & Drawers
  const [showPreview, setShowPreview] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [qcIssues, setQcIssues] = useState(null);

  // Smart phrases
  const [smartPhrases, setSmartPhrases] = useState([]);
  const [selectedPhrase, setSelectedPhrase] = useState('');

  // DOCX input ref
  const docxInputRef = useRef(null);
  const [importingDocx, setImportingDocx] = useState(false);

  // Fetch initial template details if editing
  useEffect(() => {
    const loadDetails = async () => {
      if (!templateId) {
        // Create mode
        const defaultState = {
          templateId: null,
          investigationId: investigation?.investigationId || '',
          investigationName: investigation?.investigationName || '',
          templateTitle: '',
          templateDesc:
            '<p><strong>CLINICAL HISTORY:</strong></p><p>Evaluation.</p><p><br></p><p><strong>TECHNIQUE:</strong></p><p>Standard protocol.</p><p><br></p><p><strong>FINDINGS:</strong></p><p>Normal scan without acute abnormality.</p><p><br></p><p><strong>IMPRESSION:</strong></p><p>No significant abnormality detected.</p>',
          scope: 'SHARED',
          doctorName: '',
          doctorId: null,
          isDefault: false,
          isActive: true,
          version: 1
        };
        setFormData(defaultState);
        setInitialData(defaultState);
        return;
      }

      setLoading(true);
      try {
        const res = await radiologyResultService.getTemplateDetail(templateId);
        if (res.success && res.data) {
          const d = res.data;
          const loaded = {
            templateId: d.templateId,
            investigationId: d.investigationId || investigation?.investigationId,
            investigationName: d.investigationName || investigation?.investigationName || '',
            templateTitle: d.templateTitle || d.title || '',
            templateDesc: d.templateDesc || d.content || '',
            scope: d.scope || 'SHARED',
            doctorName: d.doctorName || '',
            doctorId: d.doctorId || null,
            isDefault: !!d.isDefault,
            isActive: d.isActive !== false,
            version: d.version || 1,
            updatedAt: d.updatedAt
          };
          setFormData(loaded);
          setInitialData(loaded);
        }
      } catch (err) {
        console.error('Failed to load template detail:', err);
        setErrorMessage('Failed to load template data.');
      } finally {
        setLoading(false);
      }
    };

    loadDetails();
  }, [templateId, investigation]);

  // Load smart phrases for quick-insert
  useEffect(() => {
    const loadSmartPhrases = async () => {
      try {
        const res = await radiologyMasterConfigService.getSmartPhrases();
        if (res.success && Array.isArray(res.data)) {
          setSmartPhrases(res.data);
        }
      } catch (err) {
        console.error('Failed to load smart phrases for editor:', err);
      }
    };
    loadSmartPhrases();
  }, []);

  // Track unsaved changes
  useEffect(() => {
    if (!initialData) return;
    const isDirty =
      formData.templateTitle !== initialData.templateTitle ||
      formData.templateDesc !== initialData.templateDesc ||
      formData.isDefault !== initialData.isDefault ||
      formData.scope !== initialData.scope ||
      formData.isActive !== initialData.isActive;
    setHasUnsavedChanges(isDirty);
  }, [formData, initialData]);

  // Handle In-Place Update (Save Changes)
  const handleSaveChanges = async () => {
    if (!formData.templateTitle.trim()) {
      setErrorMessage('Template Title is required.');
      return;
    }
    if (!formData.templateDesc.trim()) {
      setErrorMessage('Template Content cannot be empty.');
      return;
    }

    setSaving(true);
    setErrorMessage(null);
    setStatusMessage(null);
    try {
      const payload = {
        investigationId: formData.investigationId,
        title: formData.templateTitle.trim(),
        templateTitle: formData.templateTitle.trim(),
        templateDesc: formData.templateDesc,
        gender: 'B',
        isDefault: formData.isDefault,
        doctorId: formData.doctorId ? parseInt(formData.doctorId, 10) : null,
        doctorName: formData.doctorName || null,
        scope: formData.scope,
        isActive: formData.isActive
      };

      if (formData.templateId) {
        // In-place Update existing
        const res = await radiologyResultService.updateTemplate(formData.templateId, payload);
        if (res.success) {
          const newVersion = (formData.version || 1) + 1;
          const updated = {
            ...formData,
            version: newVersion,
            updatedAt: new Date().toISOString()
          };
          setFormData(updated);
          setInitialData(updated);
          setHasUnsavedChanges(false);
          setStatusMessage(`✓ Template updated successfully (v${newVersion})! Version snapshot recorded.`);
          if (onSaveSuccess) onSaveSuccess(res.data);
        }
      } else {
        // Create new
        const res = await radiologyResultService.createTemplate(payload);
        if (res.success) {
          const newId = res.data?.templateId || res.data?.id;
          const created = {
            ...formData,
            templateId: newId,
            version: 1,
            updatedAt: new Date().toISOString()
          };
          setFormData(created);
          setInitialData(created);
          setHasUnsavedChanges(false);
          setStatusMessage('✓ New master template saved successfully!');
          if (onSaveSuccess) onSaveSuccess(res.data);
        }
      }
    } catch (err) {
      console.error('Failed to save template:', err);
      setErrorMessage(err.response?.data?.message || 'Error saving template changes.');
    } finally {
      setSaving(false);
    }
  };

  // Handle Save As New (Clone without overwriting existing)
  const handleSaveAsNew = async () => {
    const newTitle = window.prompt(
      'Enter new title for this cloned template variant:',
      `${formData.templateTitle} - Modified`
    );
    if (!newTitle || !newTitle.trim()) return;

    setSaving(true);
    setErrorMessage(null);
    try {
      const payload = {
        investigationId: formData.investigationId,
        title: newTitle.trim(),
        templateTitle: newTitle.trim(),
        templateDesc: formData.templateDesc,
        gender: 'B',
        isDefault: false, // cloned variant should not automatically steal default
        doctorId: formData.doctorId ? parseInt(formData.doctorId, 10) : null,
        doctorName: formData.doctorName || null,
        scope: formData.scope,
        isActive: true
      };

      const res = await radiologyResultService.createTemplate(payload);
      if (res.success) {
        const newId = res.data?.templateId || res.data?.id;
        const newObj = {
          ...formData,
          templateId: newId,
          templateTitle: newTitle.trim(),
          isDefault: false,
          version: 1,
          updatedAt: new Date().toISOString()
        };
        setFormData(newObj);
        setInitialData(newObj);
        setHasUnsavedChanges(false);
        setStatusMessage(`✓ Saved as brand new template "${newTitle.trim()}"! Original remains untouched.`);
        if (onSaveSuccess) onSaveSuccess(res.data);
      }
    } catch (err) {
      console.error('Failed to save as new template:', err);
      setErrorMessage(err.response?.data?.message || 'Failed to create new template copy.');
    } finally {
      setSaving(false);
    }
  };

  // Run Quality Check / Validation
  const handleRunQualityCheck = () => {
    const issues = [];
    const content = formData.templateDesc.toLowerCase();

    if (!formData.templateTitle.trim()) {
      issues.push('Template title is missing.');
    }
    if (!content.includes('findings')) {
      issues.push('Missing "FINDINGS" section header.');
    }
    if (!content.includes('impression')) {
      issues.push('Missing "IMPRESSION" section header.');
    }

    // Check for broken braces like {{PATIENT_NAME or {AGE}
    const brokenRegex = /{[^}]*$/m;
    if (brokenRegex.test(formData.templateDesc)) {
      issues.push('Detected potentially broken dynamic field placeholder syntax.');
    }

    if (issues.length === 0) {
      setQcIssues([]);
      setStatusMessage('✓ Quality Check Passed: All essential clinical sections & formatting valid!');
    } else {
      setQcIssues(issues);
    }
  };

  // Insert Reusable Section
  const handleInsertSection = (sectionHtml) => {
    setFormData((prev) => ({
      ...prev,
      templateDesc: prev.templateDesc + sectionHtml
    }));
    setStatusMessage(`Appended "${stripHtml(sectionHtml).slice(0, 20)}..." section.`);
  };

  // Insert Dynamic Field Token
  const handleInsertToken = (token) => {
    setFormData((prev) => ({
      ...prev,
      templateDesc: prev.templateDesc + ` <strong>${token}</strong> `
    }));
    setStatusMessage(`Inserted dynamic field token ${token}`);
  };

  // Insert Smart Phrase
  const handleInsertSmartPhrase = (phraseText) => {
    if (!phraseText) return;
    setFormData((prev) => ({
      ...prev,
      templateDesc: prev.templateDesc + `<p>${phraseText}</p>`
    }));
    setStatusMessage('Appended clinical smart phrase text.');
    setSelectedPhrase('');
  };

  // DOCX Import into Editor
  const handleDocxImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImportingDocx(true);
    setErrorMessage(null);
    try {
      const res = await radiologyResultService.extractDocx(file);
      if (res.success && res.data?.extractedHtml) {
        setFormData((prev) => ({
          ...prev,
          templateDesc: res.data.extractedHtml,
          templateTitle: prev.templateTitle || file.name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ')
        }));
        setStatusMessage(`✓ Imported Word document "${file.name}" preserving layout & tables!`);
      }
    } catch (err) {
      console.error('Failed to import DOCX in editor view:', err);
      setErrorMessage('Failed to extract Word document content.');
    } finally {
      setImportingDocx(false);
      e.target.value = '';
    }
  };

  const stripHtml = (html = '') => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  if (loading) {
    return (
      <div className="rad-editor-view-loading">
        <div className="rad-spinner"></div>
        <p>Loading Clinical Template Editor...</p>
      </div>
    );
  }

  return (
    <div className="rad-editor-view-container">
      {/* Hidden Word File Input */}
      <input
        type="file"
        accept=".docx"
        ref={docxInputRef}
        style={{ display: 'none' }}
        onChange={handleDocxImport}
      />

      {/* Top Header Bar */}
      <div className="rad-editor-view-topbar">
        <div className="rad-ev-left">
          <button type="button" className="rad-ev-back-btn" onClick={onBack}>
            ← Back to Templates
          </button>
          <div className="rad-ev-title-wrap">
            <span className="rad-ev-inv-badge">
              {investigation?.departmentName || 'Radiology'} • {investigation?.investigationName} ({investigation?.testCode || 'INV'})
            </span>
            <h2 className="rad-ev-title">
              {formData.templateTitle || 'Untitled Clinical Template'}
            </h2>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="rad-ev-right-actions">
          {formData.templateId && (
            <button
              type="button"
              className="rad-ev-btn history"
              onClick={() => setShowVersions(true)}
              title="View previous revisions and restore older versions"
            >
              📜 History (v{formData.version || 1})
            </button>
          )}

          <button
            type="button"
            className="rad-ev-btn qc"
            onClick={handleRunQualityCheck}
            title="Verify required sections and dynamic tags"
          >
            🛡️ Quality Check
          </button>

          <button
            type="button"
            className="rad-ev-btn preview"
            onClick={() => setShowPreview(true)}
            title="Preview how this report will look for a patient"
          >
            👁️ Preview Report
          </button>

          {formData.templateId && (
            <button
              type="button"
              className="rad-ev-btn save-as-new"
              disabled={saving}
              onClick={handleSaveAsNew}
              title="Save changes as a brand new copy without modifying the original"
            >
              📋 Save As New
            </button>
          )}

          <button
            type="button"
            className="rad-ev-btn save-main"
            disabled={saving}
            onClick={handleSaveChanges}
            title="Update existing template in-place and create version snapshot"
          >
            {saving ? 'Saving...' : formData.templateId ? '💾 Save Changes' : '💾 Save Template'}
          </button>
        </div>
      </div>

      {/* Notifications & Banners */}
      {statusMessage && (
        <div className="rad-ev-alert success">
          <span>{statusMessage}</span>
          <button onClick={() => setStatusMessage(null)}>✕</button>
        </div>
      )}
      {errorMessage && (
        <div className="rad-ev-alert error">
          <span>⚠️ {errorMessage}</span>
          <button onClick={() => setErrorMessage(null)}>✕</button>
        </div>
      )}

      {/* QC Issues Panel */}
      {qcIssues && qcIssues.length > 0 && (
        <div className="rad-ev-qc-panel">
          <div className="rad-ev-qc-header">
            <strong>⚠️ Quality Validation: {qcIssues.length} Warning(s) Found</strong>
            <button onClick={() => setQcIssues(null)}>✕</button>
          </div>
          <ul>
            {qcIssues.map((issue, idx) => (
              <li key={idx}>{issue}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Form Metadata Row */}
      <div className="rad-ev-meta-bar">
        <div className="rad-ev-field title-field">
          <label>
            Template Title <span className="req">*</span>
          </label>
          <input
            type="text"
            placeholder="e.g. Standard Contrast Study, Normal Baseline, Trauma Protocol..."
            value={formData.templateTitle}
            onChange={(e) => setFormData({ ...formData, templateTitle: e.target.value })}
            required
          />
        </div>

        <div className="rad-ev-field">
          <label>Scope / Access</label>
          <select
            value={formData.scope}
            onChange={(e) => setFormData({ ...formData, scope: e.target.value })}
          >
            <option value="SHARED">SHARED (All Radiologists & Technicians)</option>
            <option value="DEPARTMENT">DEPARTMENT (Modality Specific)</option>
            <option value="PERSONAL">PERSONAL (Doctor Specific)</option>
            <option value="SYSTEM">SYSTEM (Locked Standard)</option>
          </select>
        </div>

        <div className="rad-ev-field">
          <label>Doctor Specific (Optional)</label>
          <input
            type="text"
            placeholder="e.g. Dr. Rao"
            value={formData.doctorName || ''}
            onChange={(e) => setFormData({ ...formData, doctorName: e.target.value })}
          />
        </div>

        <div className="rad-ev-toggles">
          <label className="rad-ev-checkbox">
            <input
              type="checkbox"
              checked={formData.isDefault}
              onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
            />
            <span>⭐ <strong>Default Template</strong></span>
          </label>

          <label className="rad-ev-checkbox">
            <input
              type="checkbox"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
            />
            <span>Active</span>
          </label>
        </div>

        <div className="rad-ev-save-status">
          {hasUnsavedChanges ? (
            <span className="status-dirty">● Unsaved Changes</span>
          ) : (
            <span className="status-saved">✓ Saved (v{formData.version || 1})</span>
          )}
        </div>
      </div>

      {/* Quick Insert Toolbar (Sections, Dynamic Fields, Smart Phrases, DOCX) */}
      <div className="rad-ev-quick-tools">
        {/* Sections */}
        <div className="rad-tool-section">
          <span className="tool-lbl">Insert Section:</span>
          <div className="tool-btn-group">
            {REUSABLE_SECTIONS.map((sec) => (
              <button
                key={sec.label}
                type="button"
                className="tool-btn"
                onClick={() => handleInsertSection(sec.html)}
              >
                + {sec.label}
              </button>
            ))}
          </div>
        </div>

        {/* Dynamic Fields */}
        <div className="rad-tool-section">
          <span className="tool-lbl">Dynamic Fields:</span>
          <div className="tool-btn-group">
            {DYNAMIC_FIELDS.map((df) => (
              <button
                key={df.token}
                type="button"
                className="tool-btn token"
                onClick={() => handleInsertToken(df.token)}
                title={`Inserts automatic patient placeholder ${df.token}`}
              >
                {df.label}
              </button>
            ))}
          </div>
        </div>

        {/* Smart Phrase Selector */}
        <div className="rad-tool-section">
          <span className="tool-lbl">Smart Phrase:</span>
          <select
            className="tool-select-phrase"
            value={selectedPhrase}
            onChange={(e) => {
              const p = smartPhrases.find((sp) => sp.shortcut === e.target.value);
              if (p) handleInsertSmartPhrase(p.phraseText);
            }}
          >
            <option value="">Select Shortcut Macro...</option>
            {smartPhrases.map((sp) => (
              <option key={sp.smartPhraseId} value={sp.shortcut}>
                {sp.shortcut} — {sp.title || sp.shortcut}
              </option>
            ))}
          </select>
        </div>

        {/* Word Document Import */}
        <div className="rad-tool-section end">
          <button
            type="button"
            className="tool-btn docx"
            disabled={importingDocx}
            onClick={() => docxInputRef.current?.click()}
            title="Import Word document preserving headings, paragraphs, and tables"
          >
            📥 {importingDocx ? 'Importing Word...' : 'Import Word (.docx)'}
          </button>
        </div>
      </div>

      {/* Rich Clinical Editor Area */}
      <div className="rad-ev-editor-card">
        <RadiologyRichEditor
          value={formData.templateDesc}
          onChange={(html) => setFormData((prev) => ({ ...prev, templateDesc: html }))}
          placeholder="Enter structured diagnostic findings, organ descriptions, technique, and impression..."
          minHeight="540px"
        />
      </div>

      {/* Modals */}
      {showPreview && (
        <RadiologyTemplatePreviewModal
          template={formData}
          investigationName={formData.investigationName || investigation?.investigationName}
          departmentName={investigation?.departmentName}
          onClose={() => setShowPreview(false)}
        />
      )}

      {showVersions && formData.templateId && (
        <RadiologyVersionHistoryDrawer
          templateId={formData.templateId}
          templateTitle={formData.templateTitle}
          onClose={() => setShowVersions(false)}
          onRestoreSuccess={(restoredData) => {
            if (restoredData) {
              setFormData((prev) => ({
                ...prev,
                templateDesc: restoredData.content || prev.templateDesc,
                version: (prev.version || 1) + 1
              }));
              setStatusMessage('✓ Successfully restored historical version content snapshot!');
            }
          }}
        />
      )}
    </div>
  );
}
